//team-page.tsx

import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import { ErrorContext, UserContext } from "../components/app-routes";
import LoadingBackdrop from "../components/loading-backdrop";
import { growerMembersApi } from "../services/api/grower-members";
import { GrowerMember } from "../models/grower-member";
import { growerInvitesApi } from "../services/api/grower-invites";
import { GrowerInvite } from "../models/grower-invite";
import axios from "axios";
import mixpanel from "mixpanel-browser";
import Page from "../components/page";
import { getCustomLabel } from "../utils/custom-labels";

export default function TeamPage() {
  const context = useContext(UserContext);
  const { handleError } = useContext(ErrorContext);

  const grower = context.grower;
  if (!grower) throw new Error("Invalid grower");

  const showProUpsell = grower.Plan === "business";
  const isPro = grower.Plan === "pro";
  const canInvite = grower.Plan === "business" || grower.Plan === "pro";
  const isOwner =
    context.user.Growers?.some((g) => g.id === grower.id) ?? false;
  const canManageTeam = canInvite && isOwner;

  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState<GrowerMember[]>([]);
  const [email, setEmail] = useState("");
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removeMemberTarget, setRemoveMemberTarget] =
    useState<GrowerMember | null>(null);
  const [revokeInviteTarget, setRevokeInviteTarget] =
    useState<GrowerInvite | null>(null);
  const [invites, setInvites] = useState<GrowerInvite[]>([]);

  const emailDomain = (email: string) =>
    email.split("@")[1]?.toLowerCase() || "unknown";

  const load = async () => {
    setBusy(true);
    try {
      const [members, invites] = await Promise.all([
        growerMembersApi.get(),
        growerInvitesApi.getPending().catch((error) => {
          if (axios.isAxiosError(error) && error.response?.status === 403) {
            return [];
          }
          throw error;
        }),
      ]);

      setMembers(members);
      setInvites(invites);
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [snack, setSnack] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });

  const handleInvite = async () => {
    const invited = email.trim();
    if (!invited) return;

    setBusy(true);

    let snackMessage = "";

    try {
      const res = await growerInvitesApi.create(invited);
      setEmail("");

      // reload data first
      await load();

      const status = (res as any)?.status; // "active" or "pending"

      mixpanel.track(
        status === "active" ? "Team - Member Added" : "Team - Invite Sent",
        {
          growerId: grower.id,
          plan: grower.Plan,
          emailDomain: emailDomain(invited),
          status: status || "unknown",
        },
      );

      snackMessage =
        (res as any)?.status === "active"
          ? `${invited} added to team`
          : `Invite sent to ${invited}`;
    } catch (error) {
      handleError(error);
      return;
    } finally {
      setBusy(false);
    }

    // show message AFTER busy/backdrop is gone
    setTimeout(() => {
      setSnack({ open: true, message: snackMessage });
    }, 0);
  };

  const handleRemove = async (id: number) => {
    setBusy(true);
    try {
      await growerMembersApi.archive(id);
      await load();

      const removed = members.find((m) => m.id === id);

      mixpanel.track("Team - Member Removed", {
        growerId: grower.id,
        plan: grower.Plan,
        memberId: id,
        emailDomain: removed?.User?.Email
          ? emailDomain(removed.User.Email)
          : "unknown",
      });
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const handleRevokeInvite = async (id: number) => {
    setBusy(true);
    try {
      await growerInvitesApi.revoke(id);
      await load();

      const revoked = invites.find((i) => i.id === id);

      mixpanel.track("Team - Invite Revoked", {
        growerId: grower.id,
        plan: grower.Plan,
        inviteId: id,
        emailDomain: revoked?.email ? emailDomain(revoked.email) : "unknown",
      });
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title={getCustomLabel(grower ?? null, "team")}>
      {showProUpsell && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography sx={{ mb: 1 }}>
            Pro feature: Add up to 5 additional users with page-level access
            control.
          </Typography>

          <Button variant="contained" disabled>
            Configure (Pro)
          </Button>
        </Paper>
      )}

      {canManageTeam && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography sx={{ mb: 2 }}>
            Invite a new team member by email. Must use an email address not
            already registered with SeedLeaf.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              fullWidth
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={handleInvite}
              disabled={!email || !canManageTeam}
            >
              Invite
            </Button>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography sx={{ mb: 1 }}>Team members</Typography>
        <Divider sx={{ mb: 1 }} />

        <List>
          {/* Pending invites */}
          {invites.map((inv) => (
            <ListItem
              key={`invite-${inv.id}`}
              secondaryAction={
                <Button
                  onClick={() => {
                    setRemoveMemberTarget(null);
                    setRevokeInviteTarget(inv);
                    setRemoveConfirmOpen(true);
                  }}
                  variant="outlined"
                  size="small"
                  disabled={!canManageTeam}
                >
                  Revoke invite
                </Button>
              }
            >
              <ListItemText primary={inv.email} secondary="pending" />
            </ListItem>
          ))}

          {/* Active members */}
          {members.map((m) => (
            <ListItem
              key={`member-${m.id}`}
              secondaryAction={
                <Button
                  onClick={() => {
                    setRevokeInviteTarget(null);
                    setRemoveMemberTarget(m);
                    setRemoveConfirmOpen(true);
                    setRemoveConfirmOpen(true);
                  }}
                  variant="outlined"
                  size="small"
                  disabled={!canManageTeam}
                >
                  Remove
                </Button>
              }
            >
              <ListItemText
                primary={`${m.User.Name} (${m.User.Email})`}
                secondary={m.Role}
              />
            </ListItem>
          ))}

          {members.length === 0 && invites.length === 0 && (
            <ListItem>
              <ListItemText primary="No team members yet." />
            </ListItem>
          )}
        </List>
      </Paper>

      <Dialog
        open={removeConfirmOpen}
        onClose={() => setRemoveConfirmOpen(false)}
      >
        <DialogTitle>
          {revokeInviteTarget ? "Revoke invite?" : "Remove team member?"}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {revokeInviteTarget
              ? `Are you sure you want to revoke the invite for ${revokeInviteTarget.email}?`
              : removeMemberTarget
                ? `Are you sure you want to remove ${removeMemberTarget.User.Email} from this team?`
                : "Are you sure?"}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setRemoveConfirmOpen(false);
              setRemoveMemberTarget(null);
              setRevokeInviteTarget(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setRemoveConfirmOpen(false);

              if (revokeInviteTarget) {
                await handleRevokeInvite(revokeInviteTarget.id);
              } else if (removeMemberTarget) {
                await handleRemove(removeMemberTarget.id);
              }

              setRemoveMemberTarget(null);
              setRevokeInviteTarget(null);
            }}
          >
            {revokeInviteTarget ? "Revoke invite" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={snack.open}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <DialogTitle>Team</DialogTitle>

        <DialogContent>
          <DialogContentText>{snack.message}</DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSnack((s) => ({ ...s, open: false }))}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
